import { apiFetch } from './api';
import type { ChannelConnectionRow } from './channels-api';

export type HealthState = 'healthy' | 'degraded' | 'down' | 'disabled';

export interface HealthIssue {
  code: string;
  severity: 'warning' | 'critical';
  message: string;
  action?: string;
}

export interface ConnectionStats {
  failed24h: number;
  ok24h: number;
  queued: number;
  stuckQueued: number;
  inbound24h: number;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  deadJobs: number;
  windowDays: number;
}

export interface ConnectionHealth {
  connectionId: string;
  state: HealthState;
  issues: HealthIssue[];
  stats: ConnectionStats;
}

export interface SiteHealth {
  generatedAt: string;
  summary: Record<HealthState, number>;
  connections: { channel: ChannelConnectionRow; health: ConnectionHealth }[];
}

export interface DiagnosticStep {
  name: string;
  ok: boolean;
  detail: string;
}

export interface ConnectionDiagnostics {
  generatedAt: string;
  channel: ChannelConnectionRow;
  health: ConnectionHealth;
  steps: DiagnosticStep[];
  recentFailures: { messageId: string; sessionId: string; status: string; error: string | null; at: string }[];
  deadJobs: { id: string; queue: string; attempts: number; lastError: string | null; updatedAt: string }[];
}

export interface QueueStat {
  queue: string;
  pending: number;
  running: number;
  dead: number;
  oldestDueMs: number | null;
}

export interface PlatformDiagnostics {
  generatedAt: string;
  version: string;
  commit: string | null;
  process: { uptimeSec: number; rssMb: number; node: string };
  database: { ok: boolean; latencyMs: number | null };
  queues: QueueStat[];
  checks: { name: string; ok: boolean; severity: 'warning' | 'critical'; detail: string }[];
  leaders: { key: string; name: string; heldHere: boolean }[];
  connections: {
    summary: Record<HealthState, number>;
    attention: {
      siteId: string;
      siteName: string | null;
      connectionId: string;
      name: string;
      channel: string;
      health: ConnectionHealth;
    }[];
  };
}

export const siteHealth = (siteId: string) =>
  apiFetch<SiteHealth>(`/api/admin/sites/${siteId}/channels/health`);

export const runDiagnostics = (siteId: string, connectionId: string) =>
  apiFetch<ConnectionDiagnostics>(`/api/admin/sites/${siteId}/channels/${connectionId}/diagnostics`, {
    method: 'POST',
  });

export const platformDiagnostics = () => apiFetch<PlatformDiagnostics>('/api/admin/diagnostics');
